import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  getStoredUser,
  getStoredPermissions,
  isAdminRole,
  isPlatformOwnerRole,
  getFirstAllowedRoute,
} from '../../utils/permissionUtils';

const AccessDenied = () => (
  <div style={{ padding: 40, textAlign: 'center' }}>
    <h2>Access Denied</h2>
    <p>You don't have permission to view this page.</p>
  </div>
);

const ProtectedRoute = ({ children, moduleKey, action = 'view' }) => {
  const location = useLocation();
  const token = localStorage.getItem('token');
  const user = getStoredUser();
  const permissions = getStoredPermissions();

  if (!token || !user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Manage Restaurants sits above every single restaurant's own data —
  // even a superadmin (whose isAdminRole bypass below normally grants
  // everything) must NOT be let in here, so this check comes first.
  if (moduleKey === 'manage_restaurants') {
    return isPlatformOwnerRole(user) ? children : <AccessDenied />;
  }

  // Platform Owner has no business inside any single restaurant's pages —
  // bounce them back to the one page that's actually theirs.
  if (isPlatformOwnerRole(user)) {
    return location.pathname === '/manage-restaurants'
      ? children
      : <Navigate to="/manage-restaurants" replace />;
  }

  if (isAdminRole(user.role)) {
    return children;
  }

  if (!moduleKey) {
    return children;
  }

  const modulePermission = permissions?.[moduleKey];

  const allowed =
    !!modulePermission &&
    (action === 'view'
      ? modulePermission.can_view
      : action === 'create'
      ? modulePermission.can_create
      : action === 'update'
      ? modulePermission.can_update
      : action === 'delete'
      ? modulePermission.can_delete
      : false);

  if (!allowed) {
    // Authenticated, just not allowed here — send them to a page they can
    // actually see instead of bouncing to /login (which, with the token
    // still stored, looked like login silently failing every time).
    const fallbackRoute = getFirstAllowedRoute(user, permissions);

    if (fallbackRoute && fallbackRoute !== location.pathname) {
      return <Navigate to={fallbackRoute} replace />;
    }

    return <AccessDenied />;
  }

  return children;
};

export default ProtectedRoute;